import React from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  Zap, 
  Shield, 
  Award,
  ArrowRight 
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const StudentAccommodationSection = () => {
  const features = [
    'Full unit deep cleaning',
    'Kitchen and bathroom restoration',
    'End of tenancy compliance standards',
    'Linen and inventory coordination',
    'Multi-site scheduling capability',
    'Documented quality assurance',
    'Damage assessment and reporting',
  ];

  const trustHighlights = [
    { icon: Zap, text: 'Scalable for large portfolios' },
    { icon: Shield, text: 'Guaranteed deadline delivery' },
    { icon: Award, text: 'Auditable quality standards' },
  ];

  return (
    <section
      id="student-accommodation"
      className="section-padding bg-white"
      data-testid="student-accommodation-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-manrope text-sm font-semibold text-accent uppercase tracking-widest mb-4 block">
              High-Volume Operations
            </span>
            <h2
              className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 uppercase tracking-tight mb-6"
              data-testid="student-section-headline"
            >
              Property Turnover{' '}
              <span className="text-accent">Programmes</span>
            </h2>
            <p className="font-manrope text-lg text-slate-600 mb-8">
              We manage complex, high-volume property turnover programmes for student accommodation providers, 
              BTR operators, and short-term rental portfolios. Our structured approach ensures consistent 
              standards, documented accountability, and guaranteed schedule adherence — even across multi-site operations.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-center gap-3"
                  data-testid={`student-feature-${index}`}
                >
                  <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-accent" />
                  </div>
                  <span className="font-manrope text-sm text-slate-700">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* Trust Highlights */}
            <div className="flex flex-wrap gap-6 mb-8 p-6 bg-primary-light rounded-sm">
              {trustHighlights.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-manrope text-sm font-medium text-slate-800">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-accent hover:bg-accent-hover text-white font-manrope font-bold px-8 py-6 rounded-sm btn-glow"
              data-testid="student-section-cta"
            >
              Discuss Your Programme
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="img-container rounded-sm overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_hatimiri-preview/artifacts/0is42ys1_image.png"
                alt="Professional end of tenancy cleaning - clean modern accommodation interior"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-sm shadow-xl border border-slate-100"
            >
              <div className="font-oswald text-4xl font-bold text-accent mb-1">100%</div>
              <div className="font-manrope text-sm text-slate-600">On-time delivery</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StudentAccommodationSection;
